export const ANALYTICS_EVENTS = {
  copilot: {
    appLoaded: 'copilot.app.loaded',
    chatUserMessageSent: 'copilot.chat.userMessage.send',
    chatToolCallReceived: 'copilot.chat.toolCall.received',
    panelTabChanged: 'copilot.panel.tab_changed',
    toolOutcome: 'copilot.tool.outcome',
    proposedActionApproved: 'copilot.proposedAction.approved',
    proposedActionRejected: 'copilot.proposedAction.rejected',
    proposedActionExecuted: 'copilot.proposedAction.executed',
    proposedActionAlternativeSwitched: 'copilot.proposedAction.alternative_switched',
    proposedActionRevisionStarted: 'copilot.proposedAction.revision_started',
  },
  session: {
    authenticated: 'session.authenticated',
    unauthenticated: 'session.unauthenticated',
  },
  workspace: {
    patientOpened: 'workspace.patient.opened',
    patientClosed: 'workspace.patient.closed',
    visibilityChanged: 'workspace.visibility.changed',
    tabFocused: 'workspace.tab.focused',
  },
  overlay: {
    tabFocused: 'overlay.tab.focused',
  },
  carestate: {
    scopeChanged: 'carestate.scope.changed',
    requestStatusChanged: 'carestate.request.status_changed',
    requestUrgencyChanged: 'carestate.request.urgency_changed',
    requestNoteSaved: 'carestate.request.note_saved',
    problemNoteSaved: 'carestate.problem.note_saved',
    patientNoteSaved: 'carestate.patient.note_saved',
  },
  internalNote: {
    created: 'internal_note.created',
  },
  navigator: {
    patientSelected: 'navigator.patient.selected',
    searchChanged: 'navigator.search.changed',
  },
  labs: {
    wizardOpened: 'labs.wizard.opened',
    wizardStepViewed: 'labs.wizard.step_viewed',
    wizardModeSelected: 'labs.wizard.mode_selected',
    orderSubmitAttempted: 'labs.order.submit_attempted',
    testCreateResult: 'labs.test.create_result',
    orderSubmitResult: 'labs.order.submit_result',
  },
  rx: {
    wizardOpened: 'rx.wizard.opened',
    wizardStepViewed: 'rx.wizard.step_viewed',
    submitAttempted: 'rx.submit_attempted',
    submitResult: 'rx.submit_result',
    prescriptionsViewed: 'rx.prescriptions.viewed',
    prescriptionLogsOpened: 'rx.prescription.logs_opened',
    prescriptionCancelDialogOpened: 'rx.prescription.cancel_dialog_opened',
    prescriptionCancelAttempted: 'rx.prescription.cancel_attempted',
    prescriptionCancelResult: 'rx.prescription.cancel_result',
    refillApproveAttempted: 'rx.refill.approve_attempted',
    refillApproveResult: 'rx.refill.approve_result',
  },
} as const;

export type ActorT = 'clinician' | 'copilot';
export type ScopeTypeT = 'patient' | 'problem' | 'request';

export type AnalyticsContext = Readonly<{
  actor: ActorT;
  clinicianUserId?: string;
  clinicianDisplayName?: string;
  workspaceSessionId?: string;
  patientUserId?: string;
  scopeType?: ScopeTypeT;
}>;

export type AnalyticsScopeInput = Readonly<{ type: ScopeTypeT }>;

export type AnalyticsContextInput = Readonly<{
  actor: ActorT;
  patientUserId?: string;
  scope?: AnalyticsScopeInput;
}>;

export type AnalyticsEventMap = Readonly<{
  'copilot.app.loaded': Record<string, never>;
  'copilot.chat.userMessage.send': Readonly<{
    userId: string;
    textLength: number;
    source: string;
  }>;
  'copilot.chat.toolCall.received': Readonly<{
    toolCallId: string;
    tool: string;
    inputType: string;
  }>;
  'copilot.panel.tab_changed': Readonly<{
    tabId: string;
    fromTabId?: string;
  }>;
  'copilot.tool.outcome': Readonly<{
    toolCallId: string;
    toolName: string;
    outcome: 'success' | 'failure';
    durationMs?: number;
  }>;
  'copilot.proposedAction.approved': Readonly<{
    externalId: string;
    label: string;
    toolName: string;
    autoExecuted: boolean;
  }>;
  'copilot.proposedAction.rejected': Readonly<{
    externalId: string;
    label: string;
    toolName: string;
    hasReason: boolean;
  }>;
  'copilot.proposedAction.executed': Readonly<{
    externalId: string;
    label: string;
    toolName: string;
  }>;
  'copilot.proposedAction.alternative_switched': Readonly<{
    externalId: string;
    label: string;
    toolName: string;
    alternativePayloadId: string;
  }>;
  'copilot.proposedAction.revision_started': Readonly<{
    externalId: string;
    label: string;
    toolName: string;
    hasReason: boolean;
  }>;

  'session.authenticated': Readonly<{
    isAnonymous: boolean;
    hasPhoneNumber: boolean;
  }>;
  'session.unauthenticated': Record<string, never>;

  'workspace.patient.opened': Record<string, never>;
  'workspace.patient.closed': Readonly<{
    durationMs: number;
  }>;
  'workspace.visibility.changed': Readonly<{
    visibility: 'visible' | 'hidden';
  }>;
  'workspace.tab.focused': Readonly<{
    tabId: string;
    fromTabId?: string;
  }>;

  'overlay.tab.focused': Readonly<{
    tabId: string;
    fromTabId?: string;
  }>;

  'carestate.scope.changed': Readonly<{
    toScopeType: ScopeTypeT;
    fromScopeType?: ScopeTypeT;
  }>;
  'carestate.request.status_changed': Readonly<{
    requestId: string;
    oldStatus: 'Open' | 'Resolved' | 'Cancelled';
    newStatus: 'Open' | 'Resolved' | 'Cancelled';
    requestAgeMs?: number;
  }>;
  'carestate.request.urgency_changed': Readonly<{
    requestId: string;
    oldUrgency: 'Routine' | 'Urgent';
    newUrgency: 'Routine' | 'Urgent';
  }>;
  'carestate.request.note_saved': Readonly<{
    requestId: string;
    noteLength: number;
    noteDeltaLength?: number;
  }>;
  'carestate.problem.note_saved': Readonly<{
    problemId: string;
    noteLength: number;
    noteDeltaLength?: number;
  }>;
  'carestate.patient.note_saved': Readonly<{
    noteLength: number;
    noteDeltaLength?: number;
  }>;

  'internal_note.created': Readonly<{
    linkType: ScopeTypeT;
    noteLength: number;
  }>;

  'navigator.patient.selected': Readonly<{
    selectedPatientUserId: string;
    unreadCount?: number;
    urgentCount?: number;
    routineCount?: number;
  }>;
  'navigator.search.changed': Readonly<{
    queryLength: number;
  }>;

  'labs.wizard.opened': Record<string, never>;
  'labs.wizard.step_viewed': Readonly<{
    stepId: 'select-test' | 'custom-test' | 'disease-codes' | 'review' | 'confirmation';
  }>;
  'labs.wizard.mode_selected': Readonly<{
    mode: 'existing_test' | 'custom_test';
  }>;
  'labs.order.submit_attempted': Readonly<{
    mode: 'existing_test' | 'custom_test';
    hasNotes: boolean;
    notesLength?: number;
    aoeAnswerCount: number;
    prioritySet: boolean;
  }>;
  'labs.test.create_result': Readonly<{
    outcome: 'success' | 'failure';
  }>;
  'labs.order.submit_result': Readonly<{
    mode: 'existing_test' | 'custom_test';
    outcome: 'success' | 'failure';
  }>;

  'rx.wizard.opened': Record<string, never>;
  'rx.wizard.step_viewed': Readonly<{
    stepId: 'kind' | 'details' | 'patient' | 'allergies' | 'review' | 'confirmation';
  }>;
  'rx.submit_attempted': Readonly<{
    kind: 'coded' | 'compound';
    hasPharmacyNotes: boolean;
    noSubstitutions: boolean;
  }>;
  'rx.submit_result': Readonly<{
    kind: 'coded' | 'compound';
    outcome: 'success' | 'failure';
  }>;

  'rx.prescriptions.viewed': Readonly<{
    count: number;
  }>;
  'rx.prescription.logs_opened': Readonly<{
    prescriptionIdPresent: boolean;
  }>;
  'rx.prescription.cancel_dialog_opened': Readonly<{
    prescriptionIdPresent: boolean;
  }>;
  'rx.prescription.cancel_attempted': Readonly<{
    dosespotPrescriptionId: string;
  }>;
  'rx.prescription.cancel_result': Readonly<{
    dosespotPrescriptionId: string;
    outcome: 'success' | 'rejected' | 'error';
  }>;
  'rx.refill.approve_attempted': Readonly<{
    refillId: number;
  }>;
  'rx.refill.approve_result': Readonly<{
    refillId: number;
    outcome: 'success' | 'failure';
  }>;
}>;

export type AnalyticsEventName = keyof AnalyticsEventMap;

type AnalyticsEventProperties<TEvent extends AnalyticsEventName> = AnalyticsEventMap[TEvent];

export type IdentifyParams = Readonly<{
  userId: string;
  traits?: Record<string, unknown>;
}>;

export type TrackParams<TEvent extends AnalyticsEventName> =
  AnalyticsEventProperties<TEvent> extends Record<string, never>
    ? Readonly<{ name: TEvent; context: AnalyticsContextInput }>
    : Readonly<{
        name: TEvent;
        context: AnalyticsContextInput;
        properties: AnalyticsEventProperties<TEvent>;
      }>;
